import React, { useEffect, useMemo, useState } from "react";
import planningApi from "../../api/planningApi";
import reservationApi from "../../api/reservationApi";
import GestionCalendrier from "./components/GestionCalendrier";
import GestionMood from "./components/GestionMood";
import useMonthReservation from "./hooks/useMonthReservation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ICONS } from '../../utils/fontawesome';


const formatIso = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function SuiviPlanning() {
  const [selectedMonth, setSelectedMonth] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(() => formatIso(new Date()));
  const [blockages, setBlockages] = useState([]);
  const [events, setEvents] = useState([]);
  const [ateliers, setAteliers] = useState([]);
  const [zones, setZones] = useState([]);
  const [dayReservations, setDayReservations] = useState([]);
  const [filters, setFilters] = useState({ blocages: true, evenements: true, reservations: true });
  const [refreshKey, setRefreshKey] = useState(0);
  const monthReservations = useMonthReservation(selectedMonth);

  const normalizeBlocages = (items) => {
    if (!Array.isArray(items)) return [];

    return items.map((item, index) => ({
      id: item.blocage_id || item.id || `bloc-${index}`,
      blocage_id: item.blocage_id || item.id,
      type: item.type || item.nature || "Blocage",
      zone: item.affectation.zone || "unknow",
      atelier: item.affectation.atelier || "unknow",
      date_debut: item.date_debut,
      date_fin: item.date_fin,
    }));
  };

  const normalizeEvenements = (items) => {
    if (!Array.isArray(items)) return [];

    return items.map((item, index) => ({
      id: item.evenement_id || item.id || `event-${index}`,
      nom: item.nom || "Evenement",
      date_debut: item.date_debut,
      date_fin: item.date_fin,
    }));
  };

  const reservations = useMemo(() => {
    if (!Array.isArray(monthReservations)) return [];

    return monthReservations
      .filter((item) => {
        const status = (item?.status || "").toString().toUpperCase();
        if (!status) return true;
        return status !== "ANNULE";
      })
      .map((item, index) => {
        const date_atelier =
          item.date_atelier ||
          item.date ||
          item.jour ||
          item.date_debut ||
          item.start ||
          item.debut;

        const date_fin = item.date_fin || date_atelier;

        return {
          id: item.creneau_id || item.id || `resa-${index}`,
          type: item.status || "Reservation",
          date_debut: date_atelier,
          date_fin,
          nombre_reservation: Number(item.nombre_reservation) || 0,
          nombre_inscrit: Number(item.nombre_inscrit) || 0,
        };
      });
  }, [monthReservations]);

  useEffect(() => {
    let isMounted = true;
    const currentMonth = selectedMonth || new Date();
    const annee = currentMonth.getFullYear();
    const mois = currentMonth.getMonth() + 1;

    const fetchData = async () => {
      try {
        const [
          blocagesResponse,
          evenementsResponse,
        ] = await Promise.all([
          planningApi.getBlocages(),
          planningApi.getEvenements({ annee, mois }),
        ]);

        if (!isMounted) return;

        setBlockages(normalizeBlocages(blocagesResponse));
        setEvents(normalizeEvenements(evenementsResponse));
      } catch (error) {
        console.error("Erreur de récupération des blocages/événements :", error);
        if (isMounted) {
          setBlockages([]);
          setEvents([]);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [selectedMonth, refreshKey]);

  useEffect(() => {
    let isMounted = true;

    const fetchMeta = async () => {
      try {
        const [ateliersResponse, zonesResponse] = await Promise.all([
          reservationApi.getAteliers(),
          reservationApi.getZones(),
        ]);
        if (!isMounted) return;
        setAteliers(Array.isArray(ateliersResponse) ? ateliersResponse : []);
        setZones(Array.isArray(zonesResponse) ? zonesResponse : []);
      } catch (error) {
        console.error("Erreur de récupération des ateliers/zones :", error);
        if (isMounted) {
          setAteliers([]);
          setZones([]);
        }
      }
    };

    fetchMeta();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchDayReservations = async () => {
      if (!selectedDay) {
        setDayReservations([]);
        return;
      }
      try {
        const data = await reservationApi.getReservationsBySlot({
          date: selectedDay,
          filtreAnnule: true,
        });
        if (!isMounted) return;
        setDayReservations(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur de récupération des réservations du jour :", error);
        if (isMounted) setDayReservations([]);
      }
    };
    fetchDayReservations();
    return () => {
      isMounted = false;
    };
  }, [selectedDay, refreshKey]);

  const refreshPlanningData = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleDeleteBlocage = async (blocage) => {
    const blocageId = blocage?.blocage_id || blocage?.id;
    if (!blocageId) return;
    try {
      await planningApi.deleteBlocage(blocageId);
      refreshPlanningData();
    } catch (error) {
      console.error("Erreur lors de la suppression du blocage :", error);
    }
  };

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      <div className="mb-6 flex flex-col gap-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Suivi du planning de MOOD
            </h1>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600">
            
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setFilters((prev) => ({ ...prev, blocages: !prev.blocages }))
                }
                className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white hover:border-slate-300 transition text-xs"
              >
                <FontAwesomeIcon
                  icon={filters.blocages ? ICONS.toggleOn : ICONS.toggleOff}
                  className="h-4 w-4 text-orange-600"
                />
                <span>Blocages</span>
              </button>
              <button
                type="button"
                onClick={() =>
                  setFilters((prev) => ({ ...prev, evenements: !prev.evenements }))
                }
                className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white hover:border-slate-300 transition text-xs"
              >
                <FontAwesomeIcon
                  icon={filters.evenements ? ICONS.toggleOn : ICONS.toggleOff}
                  className="h-4 w-4 text-orange-600"
                />
                <span>Événements</span>
              </button>
              <button
                type="button"
                onClick={() =>
                  setFilters((prev) => ({ ...prev, reservations: !prev.reservations }))
                }
                className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white hover:border-slate-300 transition text-xs"
              >
                <FontAwesomeIcon
                  icon={filters.reservations ? ICONS.toggleOn : ICONS.toggleOff}
                  className="h-4 w-4 text-orange-600"
                />
                <span>Réservations</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          <GestionCalendrier
            month={selectedMonth}
            onMonthChange={setSelectedMonth}
            blockages={filters.blocages ? blockages : []}
            events={filters.evenements ? events : []}
            reservations={filters.reservations ? reservations : []}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
          />
        </div>
        <div className="lg:col-span-1">
          <GestionMood
            blockages={filters.blocages ? blockages : []}
            events={filters.evenements ? events : []}
            ateliers={ateliers}
            zones={zones}
            selectedDay={selectedDay}
            reservations={filters.reservations ? dayReservations : []}
            onEditBlocage={() => {}}
            onDeleteBlocage={handleDeleteBlocage}
            onEditEvenement={() => {}}
            onDeleteEvenement={() => {}}
            onCreateIndispo={refreshPlanningData}
          />
        </div>
      </div>
    </div>
  );
}

export default SuiviPlanning;
